import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

const emailSchema = z.string().email().max(255);

export const useEmailAvailability = (email: string, debounceMs: number = 500) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    // Validate email format first
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success || !email.includes('@')) {
      setIsAvailable(null);
      return;
    }

    let isCancelled = false;
    setIsChecking(true);
    
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('email')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle();

        if (error) throw error;
        
        if (!isCancelled) {
          setIsAvailable(!data);
        }
      } catch (error: any) {
        if (!isCancelled) {
          setIsAvailable(null);
        }
      } finally {
        if (!isCancelled) {
          setIsChecking(false);
        }
      }
    }, debounceMs);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [email, debounceMs]);

  return { isChecking, isAvailable };
};
